import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { useAuth } from '@erpsquad/common/runtime';

export interface IGroupChartProps {
    chartData: any[],
    height?: number,
    margin?: any,
    barWidth?: number,
    line?: boolean
}

const formatNumber = (num: number, symbol) => {
    return `${symbol} ${d3.format(".2s")(num).replace('k', 'K')}`;
};

const GroupedBarChart = ({ 
    chartData, 
    height = 500, 
    barWidth = 40, 
    margin = { top: 40, right: 20, bottom: 40, left: 50 }, 
    line = false 
}: IGroupChartProps) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);

    const maxValue = useMemo(() => Math.max(...chartData.map((d) => d.data.map(v=>v.value)).flat()), [chartData]);

    const needsHorizontalScroll = chartData.length > 12;
    const minGroupWidth = 120; 
    const calculatedChartWidth = needsHorizontalScroll 
        ? Math.max(chartData.length * minGroupWidth, containerWidth)
        : containerWidth;

    const { user } = useAuth();
    const defaultCurrencySymbol = user?.currency_data?.symbol || '';

    useEffect(() => {
        const wrapper = wrapperRef.current;

        const resizeObserver = new ResizeObserver((entries) => {
            if (entries[0]) {
                const newWidth = entries[0].contentRect.width;
                setContainerWidth(newWidth);
            }
        });

        if (wrapper) {
            resizeObserver.observe(wrapper);
        }

        return () => {
            if (wrapper) {
                resizeObserver.unobserve(wrapper);
            }
        };
    }, []);

    useEffect(() => {
        if (containerWidth === 0) return; 
        
        const chartWidth = calculatedChartWidth - margin.left - margin.right;
        const chartHeight = maxValue ? height - margin.top - margin.bottom : 0;
        
        d3.select(svgRef.current).selectAll('*').remove();
        
        const svg = d3.select(svgRef.current)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${margin.left}, ${maxValue ? margin.top : height-50})`);

        const x0 = d3.scaleBand()
            .domain(chartData.map(d => d.month))
            .range([0, chartWidth])
            .padding(0.2);

        const x1 = d3.scaleBand()
            .domain(['Total Transaction', 'Total Value', 'Average Value'])
            .range([0, x0.bandwidth()])
            .padding(0.1);

        const y = d3.scaleLinear()
            .domain([0, d3.max(chartData, d => d3.max(d.data, data => data.value))])
            .nice()
            .range([chartHeight, 0]);

        const xAxis = svg.append('g')
            .attr('class', 'x-axis')
            .attr('transform', `translate(0, ${chartHeight})`)
            .call(d3.axisBottom(x0).tickPadding(20));
            
        const yAxis = svg.append('g')
            .attr('class', 'y-axis')
            .call(d3.axisLeft(y).tickPadding(10).tickFormat((d: any) => formatNumber(d, defaultCurrencySymbol)));

        yAxis.select(".domain").attr("stroke", "none");
        xAxis.select(".domain").attr("stroke", "none");
        
        if (line) {
            svg.selectAll('.grid-line')
                .data(y.ticks())
                .enter()
                .append('line')
                .attr('class', 'grid-line')
                .attr('x1', 0)
                .attr('x2', chartWidth)
                .attr('y1', d => y(d))
                .attr('y2', d => y(d))
                .style('stroke', '#ddd')
                .style('stroke-dasharray', '3,3') // Dotted line
                .style('stroke-width', 1);
        }

        const monthGroups = svg.selectAll('.month-group')
            .data(chartData)
            .enter()
            .append('g')
            .attr('class', 'month-group')
            .attr('transform', d => `translate(${x0(d.month)}, 0)`);

        monthGroups.selectAll('rect')
            .data(d => d.data)
            .enter()
            .append('rect')
            .attr('x', d => x1(d.type) + (x1.bandwidth() - barWidth) / 2)
            .attr('y', d => y(d.value))
            .attr('width', barWidth)
            .attr('height', d => chartHeight - y(d.value))
            .attr('fill', d => d.color)
            .attr('rx', 5)
            .attr('ry', 5);

    }, [chartData, barWidth, height, calculatedChartWidth, containerWidth, line, margin, defaultCurrencySymbol, maxValue]);

    return (
        <div ref={wrapperRef} style={{ width: '100%', height: '100%' }}>
            <div style={{ position: 'relative' }}>
                <div 
                    ref={scrollContainerRef}
                        style={{ 
                            width: '100%', 
                            overflowX: needsHorizontalScroll ? 'auto' : 'hidden',
                            overflowY: 'hidden'
                        }}
                >
                    <svg 
                        ref={svgRef} 
                        width={calculatedChartWidth} 
                        height={height} 
                        viewBox={`0 0 ${calculatedChartWidth} ${height}`}
                        style={{ display: 'block' }}
                    />
                </div>
            </div>
        </div>
    );
};

export { GroupedBarChart };
export default GroupedBarChart;